import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ChevronLeft } from 'lucide-react';

import { ConfirmationModal } from '../../components/ConfirmationModal';

import NewOfferService from '../../services/promotion-offer-service';

import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../components/ToastConfig';
import { STATUS_MESSAGES } from '../../utils/api-handlers/offer-form';

const frontendUrl = process.env.REACT_APP_FRONTEND_URL;

const trialDaysOptions = [
  { name: 3, displayName: '3 Days' },
  { name: 7, displayName: '7 Days' },
  { name: 14, displayName: '14 Days' },
  { name: 30, displayName: '30 Days' },
  { name: 60, displayName: '60 Days' },
  { name: 90, displayName: '90 Days' },
];

const termMonthsOptions = [
  { name: 3, displayName: '3 months' },
  { name: 6, displayName: '6 months' },
  { name: 12, displayName: '12 months' },
  { name: 24, displayName: '24 months' },
  { name: 0, displayName: 'Unlimited' },
];

function PromotionOfferDetails() {
  const Navigate = useNavigate();
  const { id } = useParams();
  const [mode, setMode] = useState('create');
  const [data, setData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [allowEditingOffer, setAllowEditingOffer] = useState(false);
  
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset,
    getValues
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      description: 'We’ll remind you 7 days before your trial ends.',
    }
  });

  const trialDaysValue = watch('trialDays');
  const termMonthsValue = watch('termMonths');

  // 獲取優惠券資料
  useEffect(() => {
    const fetchData = async () => {
      if (!id || id === "new") {
        setMode('create');
        setAllowEditingOffer(true);
        return;
      }

      try {
        setMode('edit');
        const data = await NewOfferService.getDetails(id);
        const { amount, code, description, isActive, name, seatsLimit, termMonths, trialDays, seatsUsed } = data.data;
        if (seatsUsed === 0) {
          setAllowEditingOffer(true);
        }
        const processedData = { amount: amount/100, code: `${frontendUrl}?promo-code=${code}`, description, isActive, name, seatsLimit, termMonths, trialDays };
        setData({ ...processedData, seatsUsed });
        
        // 設置表單預設值
        Object.keys(processedData).forEach(key => {
          if (key === "amount") {
            setValue("monthlyAmountCents", data.data[key]/100);
          } else if (key === "code") {
            setValue(key, `${frontendUrl}?promo-code=${code}`);
          } else {
            setValue(key, data.data[key]);
          }
        });
      } catch (error) {
        console.error('Failed to fetch offer data:', error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          // showToast.error('無權訪問此頁面');
          // Navigate('/subscription/coupon');
          // return;
          logout();
        } else if (error?.response?.status === 400 && error?.response?.data?.errorCode === '2000') {
          Navigate('/promotion/offer');
          return;
        } else {
          showToast.error('Failed to fetch offer data.');
        }
      }
    };

    fetchData();
  }, [id, setValue, Navigate]);

  const onSubmit = async (data) => {
    const { monthlyAmountCents, seatsLimit, trialDays, termMonths, code } = data;
    const processedData = { ...data, monthlyAmountCents: Number(monthlyAmountCents)*100, seatsLimit: Number(seatsLimit), trialDays: Number(trialDays), termMonths: Number(termMonths) };
    if (mode === 'edit') {
      processedData.code = code.substring(frontendUrl.length + 12);
    }
    try {
      let res;
      if (mode === 'edit') {
        res = await NewOfferService.update(id, processedData);
        showToast.success('Offer updated successfully.');
      } else {
        res = await NewOfferService.create(processedData);
        showToast.success('Offer created successfully.');
        setTimeout(() => {
          Navigate('/promotion/offer');
        }, 1000);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else if (error?.response?.status === 400) {
        showToast.error(STATUS_MESSAGES[error?.response?.data?.errorCode] || 'Failed to save. Please try again later.');
      } else {
        showToast.error(error?.response?.data?.message || 'Failed to save. Please try again later.');
      }
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className='flex justify-between items-center'>
        <button onClick={() => Navigate(-1)} className="text-brandDefault group flex items-center w-fit p-3 -ml-3">
          <ChevronLeft size={36} className="text-brandDefault mr-1" />
          <span className="font-bold">Back</span>
        </button>
        {/* {mode === "edit" && (
          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-28 py-2 border border-brandDefault text-brandDefault rounded-xl"
            disabled={isSubmitting}
          >
            Delete
          </button>
        )} */}
      </div>

      <div className="mx-auto bg-white rounded-lg py-6">
        <div className='flex justify-between items-center mb-6'>
          <h1 className="text-2xl font-bold">
            {mode === 'edit' ? 'Edit Offer ' : 'Create Offer'}
          </h1>
          <div className='flex'>
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-28 py-2 border border-brandDefault text-brandDefault rounded-xl mr-3"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`w-28 py-2 border border-brandDefault bg-brandDefault text-white rounded-xl ${!allowEditingOffer ? "cursor-not-allowed" : ""}`}
              disabled={isSubmitting || !allowEditingOffer}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 名稱 */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Offer Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('name', { 
                  required: 'Offer Name is required.',
                })}
                className={`w-full p-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                disabled={!allowEditingOffer}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name.message}</p>
              )}
            </div>

            {/* URL */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                URL
                <span className="text-red-500 ml-1">*</span>
                <span className="text-[#757575] ml-1">(Auto-generated after save)</span>
              </label>
              <input
                {...register('code', { 
                  // required: '名稱為必填欄位'
                })}
                className={`w-full p-2 border rounded ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
                disabled
              />
              {errors.code && (
                <p className="text-red-500 text-sm">{errors.code.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Description
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                {...register('description', { 
                  required: 'Description is required.',
                })}
                className={`w-full p-2 border rounded ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                disabled={!allowEditingOffer}
              />
              {errors.description && (
                <p className="text-red-500 text-sm">{errors.description.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* Price */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Price
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className='flex items-center'>
                <input
                  {...register('monthlyAmountCents', { 
                    required: 'Price is required.',
                    pattern: {
                      value: /^[0-9]*$/,
                      message: 'Price should be a number.'
                    },
                    min: {
                      value: 1,
                      message: 'Price should be greater than 0.'
                    } 
                  })}
                  className={`w-full p-2 border rounded ${errors.monthlyAmountCents ? 'border-red-500' : 'border-gray-300'} flex-grow flex-shrink`}
                  disabled={!allowEditingOffer}
                />
                <span className='text-nowrap whitespace-nowrap flex-shrink-0'><span className='ml-2 mr-4'>USD</span><b>/Monthly</b></span>
              </div>
              {errors.monthlyAmountCents && (
                <p className="text-red-500 text-sm">{errors.monthlyAmountCents.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* Seats */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Seats
                <span className="text-red-500 ml-1">*</span>
                <span className="text-[#757575] ml-1">(Integers only, 0 = unlimited)</span>
              </label>
              <input
                {...register('seatsLimit', { 
                  required: 'Seats is required.',
                  pattern: {
                    value: /^[0-9]*$/,
                    message: 'Seats should be a number.'
                  }
                })}
                className={`p-2 border rounded ${errors.seatsLimit ? 'border-red-500' : 'border-gray-300'} w-[calc(100%-7.8125rem)]`}
                disabled={!allowEditingOffer}
              />
              {errors.seatsLimit && (
                <p className="text-red-500 text-sm">{errors.seatsLimit.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1">
              {!allowEditingOffer && (
                <>
                  <label className="block text-sm font-medium">
                    Subscribers
                  </label>
                  <span className='!mt-3 block'>{data?.seatsUsed} / {data?.seatsLimit}</span>
                </>
              )}
            </div>

            {/* Free Trial */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Free Trial
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                {...register('trialDays', { 
                  required: 'Free Trial is required.'
                })}
                value={watch('trialDays') || ''}
                className={`p-2 border rounded ${errors.trialDays ? 'border-red-500' : 'border-gray-300'} w-[calc(100%-7.8125rem)]`}
                disabled={!allowEditingOffer}
              >
                {!trialDaysValue && <option value="" disabled>Choose a Free Trial Days</option>}
                {trialDaysOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.trialDays && (
                <p className="text-red-500 text-sm">{errors.trialDays.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* Payment Period */}
            <div className="space-y-1">
              <label className="block text-sm font-medium">
                Payment Period
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                {...register('termMonths', { 
                  required: 'Payment Period is required.'
                })}
                value={watch('termMonths') || ''}
                className={`p-2 border rounded ${errors.termMonths ? 'border-red-500' : 'border-gray-300'} w-[calc(100%-7.8125rem)]`}
                disabled={!allowEditingOffer}
              >
                {!termMonthsValue && <option value="" disabled>Choose an Option</option>}
                {termMonthsOptions.map((option, index) => (
                  <option key={option.name || index} value={option.name}>
                    {option.displayName}
                  </option>
                ))}
              </select>
              {errors.termMonths && (
                <p className="text-red-500 text-sm">{errors.termMonths.message}</p>
              )}
            </div>

            {/* Empty */}
            <div className="space-y-1"></div>

            {/* 啟用狀態 */}
            <div className="space-y-1 col-span-2">
              <label className="block text-sm font-medium mb-2">
                Activation Status
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setValue('isActive', !watch('isActive'))}
                  className={`relative inline-flex h-9 w-14 items-center rounded-full transition-colors duration-300 
                    ${watch('isActive') ? 'bg-green-600 bg-opacity-20' : 'bg-gray-200'}
                    hover:bg-opacity-30
                    ${!allowEditingOffer ? "cursor-not-allowed" : ""}
                  `}
                  disabled={!allowEditingOffer}
                >
                  <span
                    className={`inline-block h-7 w-7 transform rounded-full transition-transform duration-300 ${
                      watch('isActive') ? 'translate-x-6 bg-green-600' : 'translate-x-1 bg-gray-400'
                    }`}
                  />
                  <span 
                    className={`absolute ${
                      watch('isActive')
                        ? 'left-2.5 text-green-600'
                        : 'right-2.5 text-gray-500'
                    }`}
                  >
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className={`w-28 py-2 bg-brandDefault text-white rounded-xl ${!allowEditingOffer ? "cursor-not-allowed" : ""}`}
              disabled={isSubmitting || !allowEditingOffer}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
      <ConfirmationModal
        isOpen={showCancelModal}
        setIsOpen={setShowCancelModal}
        onClose={() => Navigate('/promotion/offer')}
        onConfirm={() => setShowCancelModal(false)}
        title=""
        message="Do you want to keep editing?"
        cancelBtn="No"
        confirmBtn="Keep Editing"
      />
    </div>
  );
}

export default PromotionOfferDetails;